import { CheckCircleOutlined, EnvironmentOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import type { DeliveryItemRow, DeliveryRow, TrackReportRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hasPermission } from '@hq/permission';
import { hq, useAuthStore } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | 'DELIVERING' | 'SIGNED' | 'PART_SIGNED' | 'REJECTED';

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待发货', color: 'default' },
  PICKING: { text: '拣货中', color: 'processing' },
  PACKED: { text: '已打包', color: 'processing' },
  SHIPPED: { text: '已发货', color: 'blue' },
  DELIVERING: { text: '配送中', color: 'blue' },
  ARRIVED: { text: '已到达', color: 'cyan' },
  SIGNED: { text: '已签收', color: 'green' },
  PART_SIGNED: { text: '部分签收', color: 'orange' },
  REJECTED: { text: '已拒收', color: 'red' },
  LOST: { text: '丢失', color: 'red' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** 已闭环状态，不再允许整单签收。 */
const CLOSED_STATUSES = new Set(['SIGNED', 'PART_SIGNED', 'REJECTED', 'CANCELED', 'LOST']);

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '配送中', value: 'DELIVERING' },
  { label: '已签收', value: 'SIGNED' },
  { label: '部分签收', value: 'PART_SIGNED' },
  { label: '已拒收', value: 'REJECTED' }
];

/**
 * 配送管理（运营端）：查看全量配送单并支持整单签收。
 * 分批签收、拒收与异常拍照仍由医院端完成。
 */
export default function DeliveryOps() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [trackForm] = Form.useForm<TrackReportRequest>();

  // 配送时效达成率（headline 指标）：直接聚合配送单，避免只看单量不看履约
  const profile = useAuthStore((state) => state.profile);
  // 时效统计接口要 wl:analytics:view：无权限时不发请求，避免卡片显示 0 误导
  const canAnalytics =
    profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'wl:analytics:view') : false);

  const sla = useQuery({
    queryKey: ['ops', 'delivery', 'sla'],
    queryFn: () => hq.stats.deliverySla({}),
    enabled: canAnalytics,
    staleTime: 60 * 1000
  });

  const tracks = useQuery({
    queryKey: ['ops', 'delivery', 'track', currentId],
    enabled: Boolean(currentId) && (trackOpen || reportOpen),
    queryFn: () => hq.track.list({ deliveryId: currentId ?? '', size: 50 })
  });

  const query = useInfiniteQuery({
    queryKey: ['ops', 'delivery', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.delivery.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        deliveryStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'delivery', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.delivery.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const sign = useMutation({
    mutationFn: async (row: DeliveryRow) => {
      const data = await hq.delivery.detail(row.id);
      return hq.delivery.sign(row.id, {
        items: data.items.map((item) => ({ deliveryItemId: item.id, signQty: item.planQty, rejectQty: 0 }))
      });
    },
    onSuccess: async (result) => {
      message.success(`签收成功：${result.signNo}`);
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery'] });
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '签收失败，请稍后重试');
    }
  });

  const reportTrack = useMutation({
    mutationFn: (values: TrackReportRequest) => hq.track.report({ ...values, deliveryId: currentId ?? '' }),
    onSuccess: async (result) => {
      message.success(`轨迹已回传，配送单状态：${result.deliveryStatus}`);
      setReportOpen(false);
      trackForm.resetFields();
      await tracks.refetch();
      await detail.refetch();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery', 'list'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '轨迹回传失败')
  });

  const confirmSign = (row: DeliveryRow) =>
    modal.confirm({
      title: '整单签收',
      content: `${row.bizNo} 将按应收数量全额签收，签收后不可撤销。`,
      okText: '签收',
      cancelText: '取消',
      onOk: () => sign.mutateAsync(row)
    });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<DeliveryRow> = [
    {
      title: '配送单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '收货科室', dataIndex: 'toDeptName', width: 160, render: (value: string | null) => value || '-' },
    { title: '配送地址', dataIndex: 'toSpaceDesc', ellipsis: { showTitle: false }, render: ellipsisCell('toSpaceDesc') },
    { title: '配送人', dataIndex: 'deliverName', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'deliveryStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '计划送达', dataIndex: 'planTime', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    { title: '签收时间', dataIndex: 'signAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            详情
          </Button>
          <Button
            type="link"
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setCurrentId(row.id);
              setTrackOpen(true);
            }}
          >
            轨迹
          </Button>
          {CLOSED_STATUSES.has(row.deliveryStatus) ? null : (
            <Button type="link" onClick={() => confirmSign(row)} loading={sign.isPending}>
              签收
            </Button>
          )}
        </Space>
      )
    }
  ];

  const itemColumns: ColumnsType<DeliveryItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '应收', dataIndex: 'planQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '实收', dataIndex: 'signQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '拒收',
      dataIndex: 'rejectQty',
      width: 90,
      align: 'right',
      render: (value: number) => (value > 0 ? <Typography.Text type="danger">{formatQty(value)}</Typography.Text> : '-')
    },
    { title: '单价', dataIndex: 'price', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  return (
    <Card
      className="page-card"
      title="配送管理"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      {canAnalytics ? (
        <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="本月配送单量" value={sla.data?.deliveryCount ?? 0} suffix="单" loading={canAnalytics && sla.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="时效达成率"
              value={(sla.data?.onTimeRate ?? 0).toFixed(1)}
              suffix="%"
              loading={canAnalytics && sla.isPending}
              valueStyle={{ color: (sla.data?.onTimeRate ?? 100) < 95 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="超时单量"
              value={sla.data?.overtimeCount ?? 0}
              suffix="单"
              loading={canAnalytics && sla.isPending}
              valueStyle={{ color: (sla.data?.overtimeCount ?? 0) > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="平均送达时长" value={(sla.data?.avgHours ?? 0).toFixed(1)} suffix="小时" loading={canAnalytics && sla.isPending} />
          </Card>
        </Col>
      </Row>

      {sla.data?.rows.length ? (
        <Card size="small" title="机构时效（Top 8）" style={{ marginBottom: 16 }}>
          <Space wrap size={[8, 8]}>
            {sla.data.rows.slice(0, 8).map((row) => (
              <Tag key={`${row.supplierId}-${row.deptId}`} color={row.onTimeRate >= 95 ? 'green' : row.onTimeRate >= 90 ? 'orange' : 'red'}>
                {row.deptName ?? row.deptId} · {row.onTimeRate.toFixed(1)}%（{row.overtimeCount}/{row.deliveryCount}）
              </Tag>
            ))}
          </Space>
        </Card>
      ) : null}
        </>
      ) : null}

      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="配送单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<DeliveryRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无配送单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.delivery.bizNo ?? '配送单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.delivery.deliveryStatus]?.text ?? detail.data.delivery.deliveryStatus}
              </Descriptions.Item>
              <Descriptions.Item label="收货科室">{detail.data.delivery.toDeptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="配送地址">{detail.data.delivery.toSpaceDesc || '-'}</Descriptions.Item>
              <Descriptions.Item label="配送人">{detail.data.delivery.deliverName || '-'}</Descriptions.Item>
              <Descriptions.Item label="计划送达">{formatDisplayTime(detail.data.delivery.planTime)}</Descriptions.Item>
              <Descriptions.Item label="签收时间">{formatDisplayTime(detail.data.delivery.signAt)}</Descriptions.Item>
            </Descriptions>

            {CLOSED_STATUSES.has(detail.data.delivery.deliveryStatus) ? (
              <Button icon={<CheckCircleOutlined />} disabled>
                已完成签收
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={sign.isPending}
                onClick={() => confirmSign(detail.data.delivery)}
              >
                整单签收
              </Button>
            )}

            <Table<DeliveryItemRow>
              style={{ marginTop: 16 }}
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>

      <Drawer
        title={`配送轨迹${detail.data?.delivery.bizNo ? ` · ${detail.data.delivery.bizNo}` : ''}`}
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        width={520}
        extra={
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => {
              trackForm.setFieldsValue({ trackType: 'ARRIVE', terminal: 'PC' });
              setReportOpen(true);
            }}
          >
            回传节点
          </Button>
        }
      >
        {tracks.isLoading ? (
          <Spin />
        ) : tracks.isError ? (
          <Alert
            type="error"
            showIcon
            message="轨迹加载失败"
            description={tracks.error instanceof ApiError ? tracks.error.friendlyMessage : String(tracks.error)}
          />
        ) : (
          <Timeline
            items={(tracks.data?.items ?? []).map((row) => ({
              color: row.trackType === 'EXCEPTION' ? 'red' : row.trackType === 'SIGN' ? 'green' : 'blue',
              children: (
                <Space direction="vertical" size={2}>
                  <Space>
                    <Tag>{row.trackType}</Tag>
                    <Typography.Text strong>{formatDisplayTime(row.occurredAt)}</Typography.Text>
                  </Space>
                  <Typography.Text>{row.trackDesc || '-'}</Typography.Text>
                  <Typography.Text type="secondary">
                    {row.location || '未记录位置'} · {row.terminal || '-'}
                    {row.deviceNo ? ` · ${row.deviceNo}` : ''}
                  </Typography.Text>
                </Space>
              )
            }))}
          />
        )}
        {(tracks.data?.items.length ?? 0) === 0 && !tracks.isLoading ? (
          <Typography.Text type="secondary">暂无轨迹节点，可由配送员在 PDA / APP 回传。</Typography.Text>
        ) : null}
      </Drawer>

      <Modal
        title="回传配送节点"
        open={reportOpen}
        onCancel={() => setReportOpen(false)}
        okText="提交"
        cancelText="取消"
        confirmLoading={reportTrack.isPending}
        onOk={() => trackForm.submit()}
        destroyOnHidden
      >
        <Form form={trackForm} layout="vertical" onFinish={(values) => reportTrack.mutate(values)}>
          <Form.Item name="trackType" label="节点类型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'PICK', label: '拣货' },
                { value: 'PACK', label: '打包' },
                { value: 'SHIP', label: '发车' },
                { value: 'ARRIVE', label: '到达' },
                { value: 'SIGN', label: '签收' },
                { value: 'EXCEPTION', label: '异常' }
              ]}
            />
          </Form.Item>
          <Form.Item name="location" label="位置">
            <Input maxLength={128} placeholder="如 住院部一楼大厅" />
          </Form.Item>
          <Form.Item name="trackDesc" label="说明">
            <Input.TextArea rows={2} maxLength={255} showCount />
          </Form.Item>
          <Form.Item name="terminal" label="回传终端">
            <Select
              options={[
                { value: 'PC', label: 'PC' },
                { value: 'PDA', label: 'PDA' },
                { value: 'APP', label: 'APP' }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
